import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// 解像度の設定データ
const resolutionGroups = [
  {
    title: '縦長',
    aspectRatio: '896:1152',
    shape: { width: 896, height: 1152 },
    variations: [
      { id: 1, size: '896x1152', label: '標準' },
      { id: 2, size: '1792x2304', label: '高解像度' }
    ]
  },
  {
    title: '正方形',
    aspectRatio: '1:1',
    shape: { width: 1024, height: 1024 },
    variations: [
      { id: 3, size: '1024x1024', label: '標準' },
      { id: 4, size: '2048x2048', label: '高解像度' }
    ]
  },
  {
    title: '縦長 1.2',
    aspectRatio: '1:2',
    shape: { width: 640, height: 1280 },
    variations: [
      { id: 5, size: '640x1280', label: '標準' },
      { id: 6, size: '1280x2560', label: '高解像度' }
    ]
  },
  {
    title: '横長',
    aspectRatio: '1152:896',
    shape: { width: 1152, height: 896 },
    variations: [
      { id: 7, size: '1152x896', label: '標準' },
      { id: 8, size: '2304x1792', label: '高解像度' }
    ]
  }
];

// 解像度選択ボタンコンポーネント
const ResolutionButton = ({ onClick, selectedResolution = null }) => {
  const getPreviewDimensions = (width, height) => {
    const baseSize = 16;
    const aspectRatio = width / height;
    
    if (aspectRatio > 1) {
      return {
        width: baseSize,
        height: baseSize / aspectRatio
      };
    } else {
      return {
        width: baseSize * aspectRatio,
        height: baseSize
      };
    }
  };

  const previewDimensions = selectedResolution
    ? getPreviewDimensions(
        ...selectedResolution.size.split('x').map(num => parseInt(num))
      )
    : { width: 16, height: 16 };

  return (
    <Button
      onClick={onClick}
      variant="outlined"
      color="primary"
      sx={{
        minWidth: '200px',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        bgcolor: 'background.paper'
      }}
      endIcon={<KeyboardArrowDownIcon />}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {selectedResolution ? (
          <>
            <Box
              sx={{
                width: previewDimensions.width,
                height: previewDimensions.height,
                border: 1,
                borderColor: 'primary.main',
                flexShrink: 0
              }}
            />
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedResolution.size}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedResolution.label}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <AspectRatioIcon color="action" sx={{ fontSize: 20 }} />
            <Typography color="text.secondary">
              解像度を選択
            </Typography>
          </>
        )}
      </Box>
    </Button>
  );
};

// 解像度プレビューコンポーネント
const ResolutionDisplay = ({ width, height, selected, onClick, isMobile, isExtraSmall }) => {
  const containerSize = isExtraSmall ? 60 : isMobile ? 70 : 80;
  const aspectRatio = width / height;
  
  let displayWidth, displayHeight;
  
  if (aspectRatio > 1) {
    displayWidth = containerSize;
    displayHeight = containerSize / aspectRatio;
  } else {
    displayHeight = containerSize;
    displayWidth = containerSize * aspectRatio;
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        width: isExtraSmall ? 70 : isMobile ? 80 : 90,
        height: isExtraSmall ? 70 : isMobile ? 80 : 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          width: displayWidth,
          height: displayHeight,
          border: 1.5,
          borderColor: selected ? 'primary.main' : 'grey.300',
          bgcolor: selected ? 'primary.50' : 'background.paper',
          position: 'relative',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: selected ? 'primary.main' : 'primary.light',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: isExtraSmall ? '0.6rem' : '0.65rem',
          }}
        >
          <Typography variant="caption" align="center" sx={{ lineHeight: 1.2 }}>
            {width}<br/>x<br/>{height}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// メインの解像度選択ダイアログ
const ResolutionDialog = ({ open, onClose, onSelect }) => {
  const [selectedResolution, setSelectedResolution] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:380px)');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minWidth: isMobile ? '100%' : 600,
          m: isMobile ? 0 : 2,
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          p: isMobile ? 1 : 1.5,
          pl: isMobile ? 1.5 : 2,
          fontSize: isMobile ? '1.1rem' : '1.25rem'
        }}
      >
        解像度の選択
      </DialogTitle>
      <DialogContent 
        dividers 
        sx={{ 
          p: isMobile ? 0.5 : 1,
          ...(isMobile && {
            '::-webkit-scrollbar': {
              width: '8px',
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            }
          })
        }}
      >
        <Grid container spacing={isMobile ? 0.5 : 1}>
          {resolutionGroups.map((group) => (
            <Grid item xs={12} sm={6} key={group.title}>
              <Card 
                variant="outlined" 
                sx={{
                  height: '100%',
                  '& .MuiCardContent-root': {
                    p: isMobile ? 1 : 1.5,
                    '&:last-child': { pb: isMobile ? 1 : 1.5 }
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: isMobile ? 0.5 : 1,
                    flexWrap: 'wrap',
                    gap: 0.5
                  }}>
                    <Typography 
                      variant={isMobile ? "body1" : "subtitle1"} 
                      sx={{ fontWeight: 500, mr: 1 }}
                    >
                      {group.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.aspectRatio}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: isMobile ? 1 : 1.5,
                    flexDirection: isExtraSmall ? 'column' : 'row'
                  }}>
                    <ResolutionDisplay
                      width={group.shape.width}
                      height={group.shape.height}
                      selected={selectedResolution?.group === group.title}
                      onClick={() => setSelectedResolution({ group: group.title })}
                      isMobile={isMobile}
                      isExtraSmall={isExtraSmall}
                    />

                    <Box sx={{ flex: 1 }}>
                      {group.variations.map((variation) => (
                        <Box
                          key={variation.id}
                          onClick={() => setSelectedResolution({ ...variation, group: group.title })}
                          sx={{
                            py: 0.5,
                            px: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            bgcolor: selectedResolution?.id === variation.id ? 'primary.50' : 'background.paper',
                            border: 1,
                            borderColor: selectedResolution?.id === variation.id ? 'primary.200' : 'transparent',
                            '&:hover': {
                              bgcolor: selectedResolution?.id === variation.id ? 'primary.50' : 'action.hover',
                            },
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: isExtraSmall ? '0.8125rem' : 'inherit'
                            }}
                          >
                            {variation.size}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            display="block"
                            sx={{
                              fontSize: isExtraSmall ? '0.75rem' : 'inherit'
                            }}
                          >
                            {variation.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: isMobile ? 1 : 1.5,
        ...(isMobile && {
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider'
        })
      }}>
        <Button 
          onClick={onClose} 
          size={isMobile ? "small" : "medium"}
        >
          キャンセル
        </Button>
        <Button
          onClick={() => {
            if (selectedResolution?.id) {
              onSelect(selectedResolution);
            }
          }}
          disabled={!selectedResolution?.id}
          variant="contained"
          size={isMobile ? "small" : "medium"}
        >
          選択
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// メインのアプリコンポーネント
const ResolutionSelector = ({onSelect}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState({
    "id": 1,
    "size": "896x1152",
    "label": "標準",
    "group": "縦長 896:1152"
  });

  return (
    <Box sx={{ p: 0 }}>
      <ResolutionButton
        onClick={() => setDialogOpen(true)}
        selectedResolution={selectedResolution}
      />
      <ResolutionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(resolution) => {
          setSelectedResolution(resolution);
          let res = resolution.size.split("x")
          console.log(resolution)
          onSelect({width:parseInt(res[0]), height:parseInt(res[1])})
          setDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default ResolutionSelector;